// utils/qrFlow.js
// ─────────────────────────────────────────────────────────────────────────────
// THE single model for on-screen ordering QRs. Every overlay QR surface
// (idle-scene join CTA, Booth order card, Summon escalation) resolves through
// resolveQrFlow() — one flow per scene, one coherent story, never two
// competing QRs.
//
// FLOWS (the named set) — each row documents the intended INCENTIVE:
//
//   off    — no ordering QR anywhere. Story: "watch the show."
//   free   — client-paid private event. The guest is a PARTY GUEST, not a
//            customer: no prices, no "free/order/standby" words. /join renders
//            the party form (no tier picker, no checkout branch). Incentive:
//            maximize participation, zero commerce framing — the client
//            already paid, so a dollar sign on screen is a hosting failure.
//   paid   — public market. The guest IS a customer: /join tier picker
//            (⚡ flash $10 / 📷 photo $30 / 🕐 standby free). Pricing is
//            server-side only (createLivestreamCheckout) — copy here mirrors
//            it, never sets it. Incentive: convert queue interest into paid
//            guaranteed spots; standby stays the quiet fallback.
//   custom — arbitrary URL (campaign QR, event page, tip jar). Copy comes
//            from config/stream.customQrLabel/Sublabel. Incentive: whatever
//            the campaign is — deliberately never mixed with /join copy.
//
// STORAGE — one nullable object on the shift doc (no migration):
//
//   shift.qrFlows = {
//     mode:      "off" | "free" | "paid" | "custom",  // shift default flow
//     customUrl: null | "https://…",                  // custom-flow target
//     scenes:    { booth?, break?, startingSoon?, main? },
//                // per-scene: "default" (or absent) | "off" | a flow id —
//                // different scenes may run different flows simultaneously
//     summonEnabled: true|false,                      // depth escalation
//   }
//
// LEGACY COMPAT (load-bearing): when shift.qrFlows is ABSENT the resolver
// reconstructs the pre-model behavior exactly from the old scattered gates
// (shift.livestreamJoinEnabled, config/stream.showQrOnBooth + customQrUrl,
// showJoinLinkOnStartingScreen) so nothing changes mid-shift for shifts
// opened before this shipped. Writers of the new mode MUST mirror
// livestreamJoinEnabled (true for free/paid, false for off) so the /join
// server gate and any legacy consumer stay coherent — see
// applyQrFlowsMirror() below.
//
// SUMMON SAFETY GATE (kept from SummonBanner's first slice): the depth-driven
// ambient escalation goes live on a REAL shift only when the operator has
// explicitly written shift.qrFlows (the "finalized per-shift orderingMode
// gate" the original comment reserved). Legacy real shifts render only the
// static legacy-equivalent surface ("steady" intensity when their old gate is
// on). Test shifts + ?summonPreview keep full live/forced behavior.
// ─────────────────────────────────────────────────────────────────────────────

export const JOIN_URL = "https://thejoyofpancakes.com/join";

// Scope an on-screen ordering QR to the booth showing it (2026-09-25).
//
// A screen QR is generated per-rig and read by someone STANDING at that rig,
// so it can always name its own queue — which means a guest scanning the
// booth screen never sees the "which booth are you at?" picker, even with
// two events running. Bare /join stays the honest fallback for the printed
// banner → linktree path, where we genuinely don't know where they are.
//
// Always appended, never conditional on concurrency: the resolver ignores the
// param when only one shift is live, so this is inert on an ordinary day and
// correct the moment a second queue opens. Shifts opened before this shipped
// have no joinCode and fall back to the bare URL.
export function joinUrlFor(shift, base = JOIN_URL) {
  const code = shift?.joinCode;
  if (!code) return base;
  return `${base}${base.includes("?") ? "&" : "?"}s=${encodeURIComponent(code)}`;
}

export const QR_FLOW_IDS = ["off", "free", "paid", "custom"];
export const QR_SCENES = ["booth", "break", "startingSoon", "main"];

// Operator-facing metadata for the Shift Controls picker.
export const QR_FLOW_META = {
  off: {
    label: "Off",
    icon: "🚫",
    blurb: "No ordering QR on any screen. Also closes /join for direct links.",
  },
  free: {
    label: "Party",
    icon: "🎈",
    blurb: "Client-paid event — guests hop in line. No prices, no commerce words.",
  },
  paid: {
    label: "Market",
    icon: "🎟",
    blurb: "Public market — /join tier picker (⚡ $10 · 📷 $30 · 🕐 standby).",
  },
  custom: {
    label: "Custom link",
    icon: "🔗",
    blurb: "Point the QR at any URL (campaign, event page). Uses the QR card copy from Stream Setup.",
  },
};

// On-screen voice per flow. Menu prices mirror the server-side pricing in
// createLivestreamCheckout — update here if join pricing ever changes.
// `menu` (paid only) renders as name-left/price-right rows on EVERY paid QR
// surface — the market screens double as the booth's living price menu.
export const QR_FLOW_COPY = {
  free: {
    head: "🥞 Fancy a pancake?",
    sub: "📲 Scan to hop in line — or tap the link in bio",
    menu: null,
    tileCap: "Scan to hop in",
  },
  paid: {
    head: "🎟 Get in the queue!",
    sub: "📲 Scan the code — or tap the link in bio",
    menu: [
      { icon: "⚡", name: "Flash pancake", price: "$10" },
      { icon: "📷", name: "Your photo", price: "$30" },
      { icon: "🕐", name: "Standby", price: "Free" },
    ],
    tileCap: "Scan to order",
  },
  custom: {
    head: "📱 Scan for more",
    sub: "Point your camera at the code",
    menu: null,
    tileCap: "Scan me",
  },
};

// Empty line shouts, a healthy line whispers, a slammed line stays silent.
export function intensityForDepth(depth) {
  const n = Number(depth) || 0;
  return n === 0 ? "full" : n <= 6 ? "nudge" : "silent";
}

// URL preview overrides (kept from the first Summon slice — quick static
// checks in any scene at any time). No listener — safe to call per render.
//   ?summonPreview=full|nudge|silent  → pin an intensity
//   ?summonMode=free|paid             → pin a voice
export function readSummonPreview() {
  if (typeof window === "undefined") return { forced: null, mode: null };
  const p = new URLSearchParams(window.location.search);
  const fp = p.get("summonPreview");
  const forced = ["full", "nudge", "silent"].includes(fp) ? fp : null;
  const sm = p.get("summonMode");
  const mode = sm === "paid" ? "paid" : sm === "free" ? "free" : null;
  return { forced, mode };
}

// ── THE RESOLVER ─────────────────────────────────────────────────────────────
// resolveQrFlow(scene, shift, streamConfig) → {
//   flow:   "off" | "free" | "paid" | "custom"   (off = render nothing)
//   url:    QR target
//   copy:   { head, sub, tiers, tileCap }        (custom pulls operator copy)
//   escalate: true  → depth-driven summon ladder is live (new model / test)
//             false → render the steady legacy surface only
// }
// `scene` ∈ QR_SCENES. Join-based flows additionally require an open queue —
// a QR must never point a guest at a closed flow.
export function resolveQrFlow(scene, shift, streamConfig) {
  const OFF = { flow: "off", url: null, copy: null, escalate: false };
  const cfg = streamConfig || {};
  const customUrlCfg = (cfg.customQrUrl || "").trim();

  const copyFor = (flow) => {
    if (flow === "custom") {
      return {
        ...QR_FLOW_COPY.custom,
        head: (cfg.customQrLabel || "").trim() || QR_FLOW_COPY.custom.head,
        sub: (cfg.customQrSublabel || "").trim() || QR_FLOW_COPY.custom.sub,
      };
    }
    // Paid menu derives from Road Mode shift pricing when present (and drops
    // the standby row when the shift hides that tier) — the on-screen menu
    // must always match what /join actually charges.
    if (flow === "paid") {
      const p = shift?.pricing;
      const fmt = (c) => (Number.isFinite(c) && c > 0 ? (c % 100 === 0 ? `$${c / 100}` : `$${(c / 100).toFixed(2)}`) : null);
      const flashPrice = fmt(p?.flashCents) || "$10";
      const customPrice = fmt(p?.customCents) || "$30";
      return {
        ...QR_FLOW_COPY.paid,
        menu: [
          { icon: "⚡", name: "Flash pancake", price: flashPrice },
          { icon: "📷", name: "Your photo", price: customPrice },
          ...(p?.hideStandby === true ? [] : [{ icon: "🕐", name: "Standby", price: "Free" }]),
        ],
      };
    }
    return QR_FLOW_COPY[flow] || null;
  };

  // ── New model — shift.qrFlows present (operator has set it) ──
  const qf = shift?.qrFlows;
  if (qf && typeof qf === "object" && qf.mode) {
    const sceneVal = qf.scenes?.[scene];
    const flow =
      sceneVal && sceneVal !== "default" ? sceneVal : qf.mode;
    if (flow === "off" || !QR_FLOW_IDS.includes(flow)) return OFF;

    if (flow === "custom") {
      const url = (qf.customUrl || "").trim() || customUrlCfg;
      if (!url) return OFF; // custom with no target = nothing to point at
      return { flow, url, copy: copyFor("custom"), escalate: qf.summonEnabled !== false };
    }

    // free / paid ride /join — require an open queue.
    if (!shift?.isQueueOpen) return OFF;
    return {
      flow,
      url: joinUrlFor(shift),
      copy: copyFor(flow),
      escalate: qf.summonEnabled !== false,
    };
  }

  // ── Legacy reconstruction — no qrFlows on the shift ──
  const joinOpen = !!shift?.isQueueOpen && !!shift?.livestreamJoinEnabled;

  // Test shifts (Lab drills) keep the first Summon slice's behavior: the
  // live depth ladder runs on EVERY scene whenever the join gate is open,
  // voiced by shift.summonMode (default free) — no config gates needed.
  if (shift?._isTestData) {
    if (!joinOpen) return OFF;
    const flow = shift.summonMode === "paid" ? "paid" : "free";
    return { flow, url: joinUrlFor(shift), copy: copyFor(flow), escalate: true };
  }

  if (scene === "break") {
    // Idle-scene JoinCtaCard gate (Break portrait CTA).
    if (!joinOpen) return OFF;
    const url = customUrlCfg || joinUrlFor(shift);
    const flow = customUrlCfg ? "custom" : "paid";
    return { flow, url, copy: copyFor(flow), escalate: false };
  }

  if (scene === "startingSoon") {
    // Portrait CTA shares the join gate; the landscape custom card keeps its
    // own showJoinLinkOnStartingScreen gate — a custom URL wins when set.
    if (customUrlCfg && cfg.showJoinLinkOnStartingScreen !== false) {
      return { flow: "custom", url: customUrlCfg, copy: copyFor("custom"), escalate: false };
    }
    if (!joinOpen) return OFF;
    return { flow: "paid", url: joinUrlFor(shift), copy: copyFor("paid"), escalate: false };
  }

  if (scene === "booth") {
    // config/stream.showQrOnBooth gated on an open queue (pack-down safety).
    if (!cfg.showQrOnBooth || !shift?.isQueueOpen) return OFF;
    const url = customUrlCfg || joinUrlFor(shift);
    const flow = customUrlCfg ? "custom" : "paid";
    return { flow, url, copy: copyFor(flow), escalate: false };
  }

  // main had no legacy QR surface.
  return OFF;
}

// ── WRITER HELPER ────────────────────────────────────────────────────────────
// Fields to write alongside a qrFlows update so legacy consumers stay
// coherent: /join's server gate (livestreamJoin requires
// livestreamJoinEnabled) and every not-yet-migrated reader of the old toggle.
// custom mode leaves livestreamJoinEnabled untouched — the operator may keep
// /join independently open (e.g. link in bio) while the screens run a
// campaign QR.
export function qrFlowsWritePatch(qrFlows) {
  const patch = { qrFlows };
  if (qrFlows.mode === "free" || qrFlows.mode === "paid") {
    patch.livestreamJoinEnabled = true;
  } else if (qrFlows.mode === "off") {
    patch.livestreamJoinEnabled = false;
  }
  return patch;
}
